import React from 'react';
import { ReportData, DetailedFinding } from '../../types/report.types';
import HtmlEditor from '../HtmlEditor';
import { parseCVSSVector } from '../../../../components/forms/inputs/SeverityScoreSelector';

interface BaseFindingEditorProps {
  reportData: ReportData;
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';
  onFindingChange: (index: number, field: keyof DetailedFinding, value: string) => void;
  onRemoveFinding: (index: number) => void;
  onAddFinding: (severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Info') => void;
}

const BaseFindingEditor: React.FC<BaseFindingEditorProps> = ({
  reportData,
  severity,
  onFindingChange,
  onRemoveFinding,
  onAddFinding,
}) => {
  const findings = reportData.detailed_findings?.filter(f => {
    let fSev = (f.severity_category || 'Info').trim();
    if (!fSev || fSev.toLowerCase() === 'none') {
      fSev = 'Info';
    }
    return fSev.toLowerCase() === severity.toLowerCase();
  }) || [];

  const severityAccent = {
    Critical: 'from-red-500 to-red-700 text-red-700 bg-red-50 border-red-200',
    High: 'from-orange-400 to-orange-600 text-orange-700 bg-orange-50 border-orange-200',
    Medium: 'from-yellow-400 to-yellow-600 text-amber-700 bg-amber-50 border-amber-200',
    Low: 'from-blue-400 to-blue-700 text-blue-700 bg-blue-50 border-blue-200',
    Info: 'from-slate-400 to-slate-600 text-slate-700 bg-slate-50 border-slate-200',
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className={`w-1.5 h-8 bg-gradient-to-b ${severityAccent[severity].split(' ')[0]} ${severityAccent[severity].split(' ')[1]} rounded-full`} />
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">{severity} Findings</h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">Manage and edit all {severity.toLowerCase()} severity vulnerabilities</p>
            </div>
          </div>
          <button
            onClick={() => onAddFinding(severity)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-sm"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            Add {severity} Finding
          </button>
        </div>

        <div>
          {findings.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <p className="text-sm font-medium text-slate-600">No {severity.toLowerCase()} findings added yet.</p>
              <button
                onClick={() => onAddFinding(severity)}
                className="mt-3 text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline"
              >
                + Click to add your first {severity.toLowerCase()} finding
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {findings.map((finding, idx) => {
                const actualIndex = reportData.detailed_findings?.findIndex(f => f === finding) ?? -1;

                return (
                  <div key={idx} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all hover:border-slate-300">
                    {/* Header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-black border ${severityAccent[severity].split(' ').slice(2).join(' ')}`}>
                          {finding.abbreviation || `${severity[0]}${idx + 1}`}
                        </span>
                        <h3 className="text-sm font-bold text-slate-900 truncate">
                          {finding.title || `Finding ${idx + 1}`}
                        </h3>
                      </div>

                      <button
                        onClick={() => {
                          if (actualIndex !== -1) {
                            onRemoveFinding(actualIndex);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 text-xs font-bold rounded-lg transition-colors border border-red-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Grid 1: Basic Info */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Abbreviation */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Abbreviation</label>
                          <input
                            type="text"
                            value={finding.abbreviation || `${severity[0]}${idx + 1}`}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'abbreviation', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-medium text-slate-800"
                            placeholder={`e.g., ${severity[0]}${idx + 1}`}
                          />
                        </div>

                        {/* Title */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-700 mb-1">Title</label>
                          <input
                            type="text"
                            value={finding.title || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'title', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-medium text-slate-800"
                            placeholder="Enter finding title"
                          />
                        </div>
                      </div>

                      {/* Grid 2: Severity & Status Selectors */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Severity Category</label>
                          <select
                            value={(() => {
                              const val = (finding.severity_category || severity || 'Info').trim();
                              const normalized = val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
                              if (['Critical', 'High', 'Medium', 'Low', 'Info'].includes(normalized)) return normalized;
                              return 'Info';
                            })()}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'severity_category', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs font-bold border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-slate-800"
                          >
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                            <option value="Info">Info</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Status</label>
                          <select
                            value={(() => {
                              const val = (finding.status || '').toLowerCase().trim();
                              if (['closed', 'fixed', 'remediated', 'resolved'].includes(val)) return 'Closed';
                              if (['business accepts risk', 'business accept risk', 'risk accepted'].includes(val)) return 'Business Accepts Risk';
                              if (['non-actionable', 'business non-actionable', 'non actionable', 'business non actionable', 'business non actionable issue'].includes(val)) return 'Business Non-Actionable';
                              return 'Open';
                            })()}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'status', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs font-bold border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-slate-800"
                          >
                            <option value="Open">Open</option>
                            <option value="Closed">Closed</option>
                            <option value="Business Accepts Risk">Business Accepts Risk</option>
                            <option value="Business Non-Actionable">Business Non-Actionable</option>
                          </select>
                        </div>
                      </div>

                      {/* Grid 3: CVSS & OWASP */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">OWASP / CWE</label>
                          <input
                            type="text"
                            value={finding.owasp_category || finding.owasp || finding.category || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'owasp_category', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                            placeholder="e.g. A01:2021-Broken Access Control"
                          />
                        </div>

                    {/* 4. CVSS Score */}
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">CVSS Score</label>
                      <input
                        type="text"
                        value={(() => {
                          if (finding.cvss_score !== undefined && finding.cvss_score !== null) {
                            return String(finding.cvss_score);
                          }
                          if (finding.cvss_vector && finding.cvss_vector.includes('CVSS')) {
                            try {
                              const parsed = parseCVSSVector(finding.cvss_vector.trim());
                              if (parsed && parsed.score && parsed.score !== '-.-') {
                                return String(parsed.score);
                              }
                            } catch (_) {}
                          }
                          return '';
                        })()}
                        onChange={(e) => {
                          const actualIndex = reportData.detailed_findings?.findIndex(f => f === finding) ?? -1;
                          if (actualIndex !== -1) {
                            onFindingChange(actualIndex, 'cvss_score', e.target.value);
                          }
                        }}
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-slate-50"
                        placeholder="e.g. 7.5"
                      />
                    </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">CVSS Vector</label>
                          <input
                            type="text"
                            value={finding.cvss_vector || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'cvss_vector', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
                            placeholder="e.g. CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H"
                          />
                        </div>
                      </div>

                      {/* Scope & Target Details */}
                      <div>
                        {(() => {
                          const targetIds = (finding as any).target_ids || [];
                          const endpointValues = (finding as any).endpoint_values || [];
                          const isStructured = targetIds.length > 0 || endpointValues.length > 0;

                          if (isStructured) {
                            const targetDetails = reportData.target_details || [];
                            const relevantTargets = targetDetails.filter(td => targetIds.includes(td.target_id));
                            const targetLabels = relevantTargets.map(t => t.target_value || t.target_name || t.url).filter(Boolean);
                            const targetUrlsStr = targetLabels.length > 0 ? targetLabels.join(', ') : '';

                            return (
                              <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">Affected Target &amp; Endpoints</label>
                                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                                  {targetUrlsStr ? (
                                    <p className="text-xs text-slate-700 font-medium mb-1.5 truncate">
                                      <span className="font-bold text-slate-500">Target: </span>{targetUrlsStr}
                                    </p>
                                  ) : finding.scope ? (
                                    <p className="text-xs text-slate-700 font-medium mb-1.5 truncate">
                                      <span className="font-bold text-slate-500">Target: </span>
                                      {finding.scope.includes(' | ')
                                        ? finding.scope.split(' | ')[0].trim()
                                        : finding.scope}
                                    </p>
                                  ) : null}
                                  {endpointValues.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {endpointValues.map((ep: string, epIdx: number) => (
                                        <span key={epIdx} className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 border border-blue-200 rounded font-mono truncate max-w-[200px]">
                                          {ep}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div>
                              <label className="block text-xs font-bold text-slate-700 mb-1">Scope</label>
                              <input
                                type="text"
                                value={finding.scope || ''}
                                onChange={(e) => {
                                  if (actualIndex !== -1) onFindingChange(actualIndex, 'scope', e.target.value);
                                }}
                                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                                placeholder="Enter vulnerable URL/component"
                              />
                            </div>
                          );
                        })()}
                      </div>

                      {/* Textareas: Description, Impact, Instructions, Fix */}
                      <div className="space-y-3 pt-2">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Description / Root Cause</label>
                          <textarea
                            value={finding.description || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'description', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white resize-y"
                            rows={3}
                            placeholder="Enter finding description"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Business Impact</label>
                          <textarea
                            value={finding.impact || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'impact', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white resize-y"
                            rows={3}
                            placeholder="Enter impact of the finding"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Steps to Reproduce</label>
                          <textarea
                            value={finding.instructions || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'instructions', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white resize-y"
                            rows={3}
                            placeholder="Enter steps to reproduce the finding"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Remediation</label>
                          <textarea
                            value={finding.fix || ''}
                            onChange={(e) => {
                              if (actualIndex !== -1) onFindingChange(actualIndex, 'fix', e.target.value);
                            }}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white resize-y"
                            rows={3}
                            placeholder="Enter remediation steps"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseFindingEditor;